/**
 * WeaveIn.ai Platform - Contacts Hook
 * React hook for contact / pre-member management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  contactsAPI,
  Contact,
  ContactCreate,
  ContactUpdate,
  ContactListResponse,
  ContactListParams,
  ContactActivity,
  ContactActivityCreate,
  ContactProgramStatus,
  DuplicateCheckResult,
  DuplicateMatch,
} from '@/services/api/contacts';

interface UseContactsOptions {
  page?: number;
  page_size?: number;
  search?: string;
  program_id?: number;
  status?: string;
  assigned_to?: number;
  intake_source?: string;
  autoFetch?: boolean;
}

interface UseContactsReturn {
  contacts: Contact[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  createContact: (data: ContactCreate) => Promise<{ contact: Contact | null; duplicates: DuplicateCheckResult[] }>;
  updateContact: (id: number, data: ContactUpdate) => Promise<boolean>;
  archiveContact: (id: number) => Promise<boolean>;
  getContact: (id: number) => Promise<Contact | null>;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setFilters: (filters: Partial<UseContactsOptions>) => void;
  // Program status
  getContactPrograms: (id: number) => Promise<ContactProgramStatus[]>;
  updateContactStatus: (contactId: number, programId: number, newStatus: string, reason?: string) => Promise<boolean>;
  assignContactStaff: (contactId: number, programId: number, userId: number) => Promise<boolean>;
  // Promotion
  enrollContact: (contactId: number, programId: number, assignedTo?: number) => Promise<any>;
  // Activities
  createActivity: (contactId: number, data: ContactActivityCreate) => Promise<boolean>;
  listActivities: (contactId: number, programId?: number) => Promise<ContactActivity[]>;
  // Duplicates
  checkDuplicates: (data: { first_name: string; last_name?: string; phone?: string; email?: string; dob?: string }) => Promise<DuplicateCheckResult[]>;
  getContactDuplicates: (id: number) => Promise<DuplicateMatch[]>;
  resolveDuplicate: (contactId: number, matchId: number, resolution: string, notes?: string) => Promise<boolean>;
  mergeContact: (sourceId: number, targetId: number) => Promise<boolean>;
}

export default function useContacts(options: UseContactsOptions = {}): UseContactsReturn {
  const {
    page: initialPage = 1,
    page_size = 50,
    search: initialSearch = '',
    program_id,
    status,
    assigned_to,
    intake_source,
    autoFetch = true,
  } = options;

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [pageSize] = useState(page_size);
  const [search, setSearch] = useState(initialSearch);
  const [filters, setFiltersState] = useState({ program_id, status, assigned_to, intake_source });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ContactListParams = {
        page,
        page_size: pageSize,
        search: search || undefined,
        ...filters,
      };
      // Remove undefined values
      Object.keys(params).forEach((key) => {
        if ((params as any)[key] === undefined) delete (params as any)[key];
      });

      const response: ContactListResponse = await contactsAPI.listContacts(params);
      setContacts(response.items);
      setTotal(response.total);
      setTotalPages(response.total_pages);
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to fetch contacts';
      setError(msg);
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filters]);

  useEffect(() => {
    if (autoFetch) {
      fetchContacts();
    }
  }, [autoFetch, fetchContacts]);

  const setFilters = useCallback((newFilters: Partial<UseContactsOptions>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
    setPage(1);
  }, []);

  const createContact = async (data: ContactCreate) => {
    setLoading(true);
    setError(null);
    try {
      const result = await contactsAPI.createContact(data);
      // Refresh contact list in background — don't block the create flow
      fetchContacts().catch(() => {});
      return { contact: result as Contact, duplicates: result.duplicate_matches || [] };
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to create contact';
      setError(msg);
      return { contact: null, duplicates: [] };
    } finally {
      setLoading(false);
    }
  };

  const updateContact = async (id: number, data: ContactUpdate): Promise<boolean> => {
    try {
      await contactsAPI.updateContact(id, data);
      await fetchContacts();
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return false;
    }
  };

  const archiveContact = async (id: number): Promise<boolean> => {
    try {
      await contactsAPI.archiveContact(id);
      await fetchContacts();
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return false;
    }
  };

  const getContact = async (id: number): Promise<Contact | null> => {
    try {
      return await contactsAPI.getContact(id);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return null;
    }
  };

  const getContactPrograms = async (id: number) => {
    try { return await contactsAPI.getContactPrograms(id); }
    catch { return []; }
  };

  const updateContactStatus = async (contactId: number, programId: number, newStatus: string, reason?: string) => {
    try {
      await contactsAPI.updateContactStatus(contactId, programId, { new_status: newStatus, reason });
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return false;
    }
  };

  const assignContactStaff = async (contactId: number, programId: number, userId: number) => {
    try {
      await contactsAPI.assignContactStaff(contactId, programId, { assigned_to: userId });
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return false;
    }
  };

  const enrollContact = async (contactId: number, programId: number, assignedTo?: number) => {
    try {
      return await contactsAPI.enrollContact(contactId, programId, { assigned_to: assignedTo });
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return null;
    }
  };

  const createActivity = async (contactId: number, data: ContactActivityCreate) => {
    try {
      await contactsAPI.createContactActivity(contactId, data);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return false;
    }
  };

  const listActivities = async (contactId: number, programId?: number) => {
    try {
      const res = await contactsAPI.listContactActivities(contactId, { program_id: programId });
      return res.items;
    } catch { return []; }
  };

  const checkDuplicates = async (data: { first_name: string; last_name?: string; phone?: string; email?: string; dob?: string }) => {
    try {
      const res = await contactsAPI.checkDuplicates(data);
      return res.matches;
    } catch { return []; }
  };

  const getContactDuplicates = async (id: number) => {
    try { return await contactsAPI.getContactDuplicates(id); }
    catch { return []; }
  };

  const resolveDuplicate = async (contactId: number, matchId: number, resolution: string, notes?: string) => {
    try {
      await contactsAPI.resolveDuplicate(contactId, matchId, { resolution, notes });
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return false;
    }
  };

  const mergeContact = async (sourceId: number, targetId: number) => {
    try {
      await contactsAPI.mergeContact(sourceId, targetId);
      await fetchContacts();
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      return false;
    }
  };

  return {
    contacts, total, totalPages, page, pageSize, loading, error,
    createContact, updateContact, archiveContact, getContact,
    refetch: fetchContacts, setPage, setSearch, setFilters,
    getContactPrograms, updateContactStatus, assignContactStaff,
    enrollContact, createActivity, listActivities,
    checkDuplicates, getContactDuplicates, resolveDuplicate, mergeContact,
  };
}
